/**
 * Sekmenin sağ tık menüsü — sıralama Chrome/Safari ile aynı: önce sekmenin kendisiyle
 * ilgili işler (sabitle, çoğalt, adlandır), sonra düzen (yan yana, grup), en sonda
 * kapatma. Kapatma en altta duruyor çünkü geri alınamayan tek işlem o.
 */
import { useTranslation } from 'react-i18next';
import { Copy, Pencil, Pin, PinOff, SplitSquareHorizontal, X } from 'lucide-react';
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@/shared/ui/ContextMenu';
import { groupPalette } from '../lib/tabGroupColors';
import type { TabGroup, TabModel } from '../model/types';
import type { TabSortKey } from '../lib/tabGroups';

export interface TabContextMenuProps {
  tab: TabModel;
  groups: TabGroup[];
  inSplit: boolean;
  canSplit: boolean;
  onRenameStart: () => void;
  actions: {
    togglePin: (id: string) => void;
    duplicate: (id: string) => void;
    setSplit: (id: string | null) => void;
    groupTab: (id: string) => void;
    moveToGroup: (id: string, groupId: string | null) => void;
    close: (id: string) => void;
    closeOthers: (id: string) => void;
    closeRight: (id: string) => void;
    closeLeft: (id: string) => void;
    closeAll: () => void;
    sortBy: (key: TabSortKey) => void;
  };
}

export function TabContextMenu({
  tab,
  groups,
  inSplit,
  canSplit,
  onRenameStart,
  actions,
}: TabContextMenuProps) {
  const { t } = useTranslation();
  const others = groups.filter((g) => g.groupId !== tab.groupId);

  return (
    <ContextMenuContent className="w-60">
      <ContextMenuItem onSelect={() => actions.togglePin(tab.tabId)}>
        {tab.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
        {tab.pinned ? t('shell.tab.unpin') : t('shell.tab.pin')}
      </ContextMenuItem>
      <ContextMenuItem onSelect={() => actions.duplicate(tab.tabId)}>
        <Copy className="h-4 w-4" />
        {t('shell.tab.duplicate')}
      </ContextMenuItem>
      <ContextMenuItem disabled={tab.pinned} onSelect={() => setTimeout(onRenameStart, 0)}>
        <Pencil className="h-4 w-4" />
        {t('shell.tab.rename')}
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuItem
        disabled={!canSplit && !inSplit}
        onSelect={() => actions.setSplit(inSplit ? null : tab.tabId)}
      >
        <SplitSquareHorizontal className="h-4 w-4" />
        {inSplit ? t('shell.tab.splitOff') : t('shell.tab.splitRight')}
      </ContextMenuItem>

      <ContextMenuSub>
        <ContextMenuSubTrigger>
          <span className="h-4 w-4" />
          {t('shell.tab.group.moveTo')}
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="w-56">
          <ContextMenuItem onSelect={() => actions.groupTab(tab.tabId)}>
            {t('shell.tab.group.new')}
          </ContextMenuItem>
          {others.length ? <ContextMenuSeparator /> : null}
          {others.map((g) => (
            <ContextMenuItem key={g.groupId} onSelect={() => actions.moveToGroup(tab.tabId, g.groupId)}>
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: groupPalette(g.color).bg }} />
              <span className="truncate">{g.name || t('shell.tab.group.untitled')}</span>
            </ContextMenuItem>
          ))}
          {tab.groupId ? (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem onSelect={() => actions.moveToGroup(tab.tabId, null)}>
                {t('shell.tab.group.remove')}
              </ContextMenuItem>
            </>
          ) : null}
        </ContextMenuSubContent>
      </ContextMenuSub>

      <ContextMenuSeparator />

      <ContextMenuItem onSelect={() => actions.close(tab.tabId)}>
        <X className="h-4 w-4" />
        {t('shell.tab.close')}
      </ContextMenuItem>
      <ContextMenuItem onSelect={() => actions.closeOthers(tab.tabId)}>
        <span className="h-4 w-4" />
        {t('shell.tab.closeOthers')}
      </ContextMenuItem>
      <ContextMenuItem onSelect={() => actions.closeRight(tab.tabId)}>
        <span className="h-4 w-4" />
        {t('shell.tab.closeRight')}
      </ContextMenuItem>
      <ContextMenuItem onSelect={() => actions.closeLeft(tab.tabId)}>
        <span className="h-4 w-4" />
        {t('shell.tab.closeLeft')}
      </ContextMenuItem>
      <ContextMenuItem onSelect={() => actions.closeAll()}>
        <span className="h-4 w-4" />
        {t('shell.tab.closeAll')}
      </ContextMenuItem>

      <ContextMenuSeparator />

      <ContextMenuSub>
        <ContextMenuSubTrigger>
          <span className="h-4 w-4" />
          {t('shell.tab.sort.label')}
        </ContextMenuSubTrigger>
        <ContextMenuSubContent>
          <ContextMenuLabel>{t('shell.tab.sort.hint')}</ContextMenuLabel>
          <ContextMenuItem onSelect={() => actions.sortBy('caption')}>
            {t('shell.tab.sort.caption')}
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => actions.sortBy('created')}>
            {t('shell.tab.sort.created')}
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => actions.sortBy('recent')}>
            {t('shell.tab.sort.recent')}
          </ContextMenuItem>
        </ContextMenuSubContent>
      </ContextMenuSub>
    </ContextMenuContent>
  );
}
